import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import styles from './SpecificSchedule.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import Schedule from './Schedule';
import clsx from 'clsx';
import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import { uniq } from 'lodash';
import Switch from 'components/UI/Switch';
import { DaySchedule } from './MedicalSchedule';
import { AlertNewBooking } from 'components/CreateBooking/UI/AlertNewBooking';
import { useTimeZone } from 'hooks';

interface SpecificScheduleProps {
    from?: Moment | undefined;
    to?: Moment | undefined;
    schedule: Array<DaySchedule>;
    onUpdate: (schedule: Array<DaySchedule>) => void;
    onHasAlerts?: (hasAlerts: boolean) => void;
}

const SpecificSchedule: FunctionComponent<SpecificScheduleProps> = ({ from, to, schedule, onUpdate, onHasAlerts }) => {
    const timezone = useTimeZone();
    const [daySchedules, setDaySchedules] = useState<DaySchedule[]>([]);

    useEffect(() => {
        if (schedule && schedule.length > 0) {
            const normalizedSchedule = schedule.map(day => {
                if (!day.doses || day.doses.length === 0) {
                    return { ...day, doses: ['8:00AM'] };
                }
                return day;
            });
            setDaySchedules(normalizedSchedule);
        } else if (from && to) {
            const days: DaySchedule[] = [];
            const current = from.clone();

            while (current.isSameOrBefore(to, 'day')) {
                days.push({
                    date: current.clone(),
                    enabled: false,
                    doses: ['8:00AM']
                });
                current.add(1, 'day');
            }

            setDaySchedules(days);
        }
    }, [from, to, schedule]);

    const handleToggleDay = (dayIndex: number, enabled: boolean) => {
        const newDaySchedules = daySchedules.map((day, idx) => {
            if (idx === dayIndex) {
                if (enabled && (!day.doses || day.doses.length === 0)) {
                    return { ...day, enabled, doses: ['8:00AM'] };
                }
                return { ...day, enabled };
            }
            return day;
        });
        setDaySchedules(newDaySchedules);
        updateSchedule(newDaySchedules);
    };

    const handleAddDose = (dayIndex: number) => {
        const newDaySchedules = [...daySchedules];
        const currentDoses = newDaySchedules[dayIndex].doses;

        const scheduleMinutes = currentDoses.map(time => {
            const [timePart, period] = time.split(/(am|pm)/i);
            const [hours, minutes] = timePart.split(':').map(Number);
            let hour24 = hours;
            if (period.toUpperCase() === 'PM' && hours !== 12) {
                hour24 += 12;
            } else if (period.toUpperCase() === 'AM' && hours === 12) {
                hour24 = 0;
            }
            return hour24 * 60 + minutes;
        });

        const maxMinutes = Math.max(...scheduleMinutes);
        const nextMinutes = maxMinutes + 15;

        const nextHour = Math.floor(nextMinutes / 60) % 24;
        const nextMinute = nextMinutes % 60;

        const nextDate = new Date();
        nextDate.setHours(nextHour, nextMinute, 0, 0);

        const newTime = nextDate
            .toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            })
            .replace(' ', '')
            .toUpperCase();

        newDaySchedules[dayIndex].doses.push(newTime);
        setDaySchedules(newDaySchedules);
        updateSchedule(newDaySchedules);
    };

    const handleRemoveDose = (dayIndex: number, doseIndex: number) => {
        const newDaySchedules = [...daySchedules];
        newDaySchedules[dayIndex].doses = newDaySchedules[dayIndex].doses.filter((_, i) => i !== doseIndex);
        setDaySchedules(newDaySchedules);
        updateSchedule(newDaySchedules);
    };

    const handleDoseTimeChange = (dayIndex: number, doseIndex: number, time: string) => {
        const newDaySchedules = [...daySchedules];
        newDaySchedules[dayIndex].doses[doseIndex] = time;
        setDaySchedules(newDaySchedules);
        updateSchedule(newDaySchedules);
    };

    const updateSchedule = (newDaySchedules: DaySchedule[]) => {
        onUpdate(newDaySchedules);
    };

    const isDoseOutOfRange = useCallback(
        (daySchedule: DaySchedule, dose: string): boolean => {
            if (!from || !to) return false;

            const isSameDayAsFrom = daySchedule.date.isSame(from, 'day');
            const isSameDayAsTo = daySchedule.date.isSame(to, 'day');

            if (!isSameDayAsFrom && !isSameDayAsTo) return false;

            const normalizedTime = dose.trim().toUpperCase();
            const parsed = moment(normalizedTime, ['h:mm A', 'hh:mm A', 'h:mmA', 'hh:mmA', 'H:mm', 'HH:mm'], true);

            if (!parsed.isValid()) return false;

            const baseDate = daySchedule.date.clone().tz(timezone);
            const hour24 = parsed.hour();
            const minute = parsed.minute();

            const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
            const period = hour24 >= 12 ? 'PM' : 'AM';

            const selectedHour = `${hour12.toString().padStart(2, '0')}:${minute
                .toString()
                .padStart(2, '0')} ${period}`;

            const doseMoment = moment.tz(
                `${baseDate.format('YYYY-MM-DD')} ${selectedHour.toUpperCase()}`,
                'YYYY-MM-DD hh:mm A',
                timezone
            );

            if (isSameDayAsFrom) {
                if (doseMoment.isBefore(from, 'minute')) return true;
            }

            if (isSameDayAsTo) {
                if (doseMoment.isAfter(to, 'minute')) return true;
            }

            return false;
        },
        [from, to, timezone]
    );

    useEffect(() => {
        if (!onHasAlerts) return;

        const hasAlerts = daySchedules.some(daySchedule => {
            if (!daySchedule.enabled) return false;

            return daySchedule.doses.some(dose => isDoseOutOfRange(daySchedule, dose));
        });

        onHasAlerts(hasAlerts);
    }, [daySchedules, isDoseOutOfRange, onHasAlerts, from, to]);

    return (
        <div className={styles.specificScheduleContainer}>
            {daySchedules.map((daySchedule, dayIndex) => (
                <div
                    key={dayIndex}
                    className={styles.dayContainer}
                >
                    <div className={styles.dayHeader}>
                        <span className={styles.dayLabel}>{daySchedule.date.format('dddd, DD MMM YYYY')}</span>
                        <Switch
                            checked={daySchedule.enabled}
                            onChange={checked => handleToggleDay(dayIndex, checked)}
                        />
                    </div>

                    {daySchedule.enabled && (
                        <div className={styles.doseSchedule}>
                            {(daySchedule.doses && daySchedule.doses.length > 0
                                ? uniq(daySchedule.doses)
                                : ['8:00AM']
                            ).map((dose, doseIndex) => {
                                const showAlert = isDoseOutOfRange(daySchedule, dose);
                                return (
                                    <>
                                        <div
                                            key={doseIndex}
                                            className={styles.doseRow}
                                        >
                                            <div className={styles.doseLabelContainer}>
                                                <span className={styles.doseLabel}>Dose {doseIndex + 1}</span>
                                                <span className={clsx(styles.doseLabel, styles.doseTime)}>{dose}</span>
                                            </div>

                                            <Schedule
                                                id={dayIndex * 100 + doseIndex}
                                                value={dose}
                                                onChange={time => handleDoseTimeChange(dayIndex, doseIndex, time)}
                                                placeholder="Select time"
                                                usedTimes={daySchedule.doses}
                                            />

                                            {doseIndex > 0 && (
                                                <FontAwesomeIcon
                                                    icon={faClose}
                                                    className={styles.removeDose}
                                                    onClick={() => handleRemoveDose(dayIndex, doseIndex)}
                                                />
                                            )}
                                        </div>
                                        {showAlert && (
                                            <AlertNewBooking
                                                className={styles.alert}
                                                bWidth="2px"
                                                icon={faExclamationTriangle}
                                                iconSize="lg"
                                                iconColor="#EAB464"
                                                bgColor="#F8F5F1"
                                                bdColor="#BCB8AE"
                                            >
                                                <span className={styles.alertText}>
                                                    Dose time must be within boarding stay hours.
                                                </span>
                                            </AlertNewBooking>
                                        )}
                                    </>
                                );
                            })}

                            <button
                                className={styles.addDoseButton}
                                onClick={() => handleAddDose(dayIndex)}
                            >
                                + Add Dose
                            </button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default SpecificSchedule;
